import * as THREE from 'three';

export class Room {
  public group: THREE.Group = new THREE.Group();

  // Animated elements
  private rainParticles: THREE.Points;
  private rainPositions!: Float32Array;
  private rainCount: number = 1200;
  private spinners: { group: THREE.Group; speed: number; startX: number; endX: number; y: number; z: number }[] = [];
  private billboardMats: THREE.MeshBasicMaterial[] = [];
  private time: number = 0;

  constructor() {
    this.buildRoomWalls();
    this.buildWindowAndFrame();
    this.buildCitySkyline();
    this.rainParticles = this.buildRain();
    this.buildSpinners();
    this.buildRoomLighting();
  }

  private buildRoomWalls() {
    const wallMat = new THREE.MeshStandardMaterial({
      color: 0x080a10,
      roughness: 0.8,
      metalness: 0.3,
    });

    const floorMat = new THREE.MeshStandardMaterial({
      color: 0x0a0c14,
      roughness: 0.3,
      metalness: 0.7,
    });

    // Floor
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), floorMat);
    floor.position.set(0, -1.35, 0);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.group.add(floor);

    // Floor neon seam lines
    const seamGeo = new THREE.PlaneGeometry(0.04, 16);
    const seamMat = new THREE.MeshBasicMaterial({ color: 0x00fff2, transparent: true, opacity: 0.4 });
    const seam1 = new THREE.Mesh(seamGeo, seamMat);
    seam1.position.set(-3.5, -1.34, 0);
    seam1.rotation.x = -Math.PI / 2;
    this.group.add(seam1);

    const seam2 = new THREE.Mesh(seamGeo, seamMat);
    seam2.position.set(3.5, -1.34, 0);
    seam2.rotation.x = -Math.PI / 2;
    this.group.add(seam2);

    // Ceiling
    const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), wallMat);
    ceiling.position.set(0, 4.5, 0);
    ceiling.rotation.x = Math.PI / 2;
    this.group.add(ceiling);

    // Left Wall
    const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(16, 6), wallMat);
    leftWall.position.set(-6, 1.6, 0);
    leftWall.rotation.y = Math.PI / 2;
    this.group.add(leftWall);

    // Right Wall
    const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(16, 6), wallMat);
    rightWall.position.set(6, 1.6, 0);
    rightWall.rotation.y = -Math.PI / 2;
    this.group.add(rightWall);

    // Back wall sections (flanking the central panoramic window)
    const backLeft = new THREE.Mesh(new THREE.PlaneGeometry(3.5, 6), wallMat);
    backLeft.position.set(-4.25, 1.6, -3.2);
    this.group.add(backLeft);

    const backRight = new THREE.Mesh(new THREE.PlaneGeometry(3.5, 6), wallMat);
    backRight.position.set(4.25, 1.6, -3.2);
    this.group.add(backRight);

    // Overhead neon lights
    const overheadLightGeo = new THREE.BoxGeometry(0.06, 0.04, 5);
    const overheadMat = new THREE.MeshBasicMaterial({ color: 0x00fff2 });
    const overhead1 = new THREE.Mesh(overheadLightGeo, overheadMat);
    overhead1.position.set(-2.5, 4.4, 0);
    this.group.add(overhead1);

    const overhead2 = new THREE.Mesh(overheadLightGeo, overheadMat);
    overhead2.position.set(2.5, 4.4, 0);
    this.group.add(overhead2);
  }

  private buildWindowAndFrame() {
    // Window opening is from x = -2.5 to +2.5, y = -0.5 to 3.8, z = -3.2
    const frameMat = new THREE.MeshStandardMaterial({
      color: 0x05070d,
      roughness: 0.5,
      metalness: 0.8,
    });

    // Window Outer Trim
    const topBar = new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.15, 0.15), frameMat);
    topBar.position.set(0, 3.8, -3.2);
    this.group.add(topBar);

    const bottomBar = new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.15, 0.15), frameMat);
    bottomBar.position.set(0, -0.6, -3.2);
    this.group.add(bottomBar);

    // Window Mullions (Vertical Dividers)
    [-1.6, 0, 1.6].forEach((x) => {
      const mullion = new THREE.Mesh(new THREE.BoxGeometry(0.1, 4.4, 0.15), frameMat);
      mullion.position.set(x, 1.6, -3.2);
      this.group.add(mullion);
    });

    // Glass Pane
    const glassGeo = new THREE.PlaneGeometry(5, 4.4);
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0x112233,
      transmission: 0.88,
      opacity: 0.85,
      transparent: true,
      roughness: 0.15,
      metalness: 0.2,
      reflectivity: 0.7,
    });
    const glass = new THREE.Mesh(glassGeo, glassMat);
    glass.position.set(0, 1.6, -3.22);
    this.group.add(glass);
  }

  private buildCitySkyline() {
    const cityGroup = new THREE.Group();
    cityGroup.position.set(0, 0, -18);

    // Distant Skyscrapers
    const buildingColors = [0x050811, 0x070c18, 0x04060c, 0x0a1020];

    for (let i = 0; i < 48; i++) {
      const w = 1.2 + Math.random() * 2.5;
      const h = 5 + Math.random() * 16;
      const d = 1.2 + Math.random() * 2.5;
      const x = (Math.random() - 0.5) * 36;
      const z = -Math.random() * 24;
      const y = h / 2 - 4;

      const bColor = buildingColors[Math.floor(Math.random() * buildingColors.length)];
      const bMat = new THREE.MeshStandardMaterial({
        color: bColor,
        roughness: 0.6,
        metalness: 0.5,
      });

      const building = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), bMat);
      building.position.set(x, y, z);
      cityGroup.add(building);

      // Window grid lights on building faces
      if (Math.random() > 0.3) {
        const winGeo = new THREE.PlaneGeometry(w * 0.85, h * 0.8);
        const winCanvas = document.createElement('canvas');
        winCanvas.width = 64;
        winCanvas.height = 128;
        const wCtx = winCanvas.getContext('2d')!;
        wCtx.fillStyle = '#000000';
        wCtx.fillRect(0, 0, 64, 128);

        const rows = 16;
        const cols = 8;
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            if (Math.random() > 0.6) {
              wCtx.fillStyle = Math.random() > 0.5 ? '#00fff2' : '#ff007f';
              wCtx.fillRect(c * 8 + 1, r * 8 + 1, 6, 6);
            }
          }
        }

        const winTex = new THREE.CanvasTexture(winCanvas);
        const winMat = new THREE.MeshBasicMaterial({
          map: winTex,
          transparent: true,
          opacity: 0.85,
        });
        const winMesh = new THREE.Mesh(winGeo, winMat);
        winMesh.position.set(x, y, z + d / 2 + 0.05);
        cityGroup.add(winMesh);
      }
    }

    // Holographic Cyber Billboards
    this.buildBillboard(cityGroup, -5, 6, -12, 'TOILLE 2088', '#00fff2');
    this.buildBillboard(cityGroup, 4.5, 7.5, -15, 'サイバー RACER', '#ff007f');
    this.buildBillboard(cityGroup, 0, 8.5, -20, 'EDGE TERMINAL', '#39ff14');

    this.group.add(cityGroup);
  }

  private buildBillboard(parent: THREE.Group, x: number, y: number, z: number, text: string, color: string) {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = '#05070d';
    ctx.fillRect(0, 0, 512, 128);
    ctx.strokeStyle = color;
    ctx.lineWidth = 8;
    ctx.strokeRect(6, 6, 500, 116);

    ctx.font = 'bold 44px "Orbitron", sans-serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.fillText(text, 256, 78);

    const tex = new THREE.CanvasTexture(canvas);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: 0.9,
    });
    this.billboardMats.push(mat);

    const billboard = new THREE.Mesh(new THREE.PlaneGeometry(6, 1.5), mat);
    billboard.position.set(x, y, z);
    parent.add(billboard);
  }

  private buildRain(): THREE.Points {
    const geo = new THREE.BufferGeometry();
    this.rainPositions = new Float32Array(this.rainCount * 3);

    for (let i = 0; i < this.rainCount; i++) {
      // Rain particles falling just outside the window between z = -3.5 and -8.0
      this.rainPositions[i * 3] = (Math.random() - 0.5) * 8; // x
      this.rainPositions[i * 3 + 1] = Math.random() * 8 - 1; // y
      this.rainPositions[i * 3 + 2] = -3.4 - Math.random() * 4; // z
    }

    geo.setAttribute('position', new THREE.BufferAttribute(this.rainPositions, 3));

    const mat = new THREE.PointsMaterial({
      color: 0x00fff2,
      size: 0.05,
      transparent: true,
      opacity: 0.65,
    });

    const rain = new THREE.Points(geo, mat);
    this.group.add(rain);
    return rain;
  }

  private buildSpinners() {
    // 3 Flying Spinners (cyberpunk hover cars) with headlight and taillight trails
    const spinnerConfigs = [
      { y: 3.2, z: -10, speed: 6.5, color: '#00fff2', dir: 1 },
      { y: 5.5, z: -14, speed: 4.8, color: '#ff007f', dir: -1 },
      { y: 1.8, z: -8, speed: 7.2, color: '#ffaa00', dir: 1 },
    ];

    spinnerConfigs.forEach((cfg) => {
      const spinnerGroup = new THREE.Group();

      // Vehicle body
      const bodyMat = new THREE.MeshBasicMaterial({ color: 0x111622 });
      const body = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.2, 0.4), bodyMat);
      spinnerGroup.add(body);

      // Headlight (Cyan / Yellow)
      const headMat = new THREE.MeshBasicMaterial({ color: 0x00ffff });
      const head = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.08, 0.35), headMat);
      head.position.set(cfg.dir > 0 ? 0.4 : -0.4, 0, 0);
      spinnerGroup.add(head);

      // Taillight (Red / Pink)
      const tailMat = new THREE.MeshBasicMaterial({ color: 0xff0055 });
      const tail = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.08, 0.35), tailMat);
      tail.position.set(cfg.dir > 0 ? -0.4 : 0.4, 0, 0);
      spinnerGroup.add(tail);

      // Light trail
      const trailMat = new THREE.MeshBasicMaterial({
        color: new THREE.Color(cfg.color),
        transparent: true,
        opacity: 0.6,
      });
      const trail = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 0.05), trailMat);
      trail.position.set(cfg.dir > 0 ? -1.2 : 1.2, 0, 0);
      trail.rotation.y = Math.PI / 2;
      spinnerGroup.add(trail);

      spinnerGroup.position.set(cfg.dir > 0 ? -15 : 15, cfg.y, cfg.z);
      this.group.add(spinnerGroup);

      this.spinners.push({
        group: spinnerGroup,
        speed: cfg.speed * cfg.dir,
        startX: cfg.dir > 0 ? -16 : 16,
        endX: cfg.dir > 0 ? 16 : -16,
        y: cfg.y,
        z: cfg.z,
      });
    });
  }

  private buildRoomLighting() {
    // Ambient dark cyber light
    const ambientLight = new THREE.AmbientLight(0x0a1020, 1.2);
    this.group.add(ambientLight);

    // Neon Pink Accent light from top left
    const pinkLight = new THREE.PointLight(0xff007f, 2.5, 9);
    pinkLight.position.set(-3, 3, 0);
    this.group.add(pinkLight);

    // Neon Cyan Accent light from top right
    const cyanLight = new THREE.PointLight(0x00fff2, 2.5, 9);
    cyanLight.position.set(3, 3, 0);
    this.group.add(cyanLight);

    // Exterior City Sky Light coming through window
    const windowLight = new THREE.DirectionalLight(0x1a3355, 1.8);
    windowLight.position.set(0, 4, -8);
    windowLight.target.position.set(0, 0, 0);
    this.group.add(windowLight);
    this.group.add(windowLight.target);
  }

  public update(deltaTime: number) {
    this.time += deltaTime;

    // Animate Rain
    const pos = this.rainPositions;
    for (let i = 0; i < this.rainCount; i++) {
      pos[i * 3 + 1] -= deltaTime * 12; // fall speed
      pos[i * 3] += deltaTime * 0.4; // slight wind angle
      if (pos[i * 3 + 1] < -1.2) {
        pos[i * 3 + 1] = 7;
        pos[i * 3] = (Math.random() - 0.5) * 8;
      }
    }
    this.rainParticles.geometry.attributes.position.needsUpdate = true;

    // Animate Flying Spinners
    this.spinners.forEach((sp) => {
      sp.group.position.x += sp.speed * deltaTime;
      // Slight aerodynamic hover sway
      sp.group.position.y = sp.y + Math.sin(this.time * 3 + sp.z) * 0.1;

      if (sp.speed > 0 && sp.group.position.x > sp.endX) {
        sp.group.position.x = sp.startX;
      } else if (sp.speed < 0 && sp.group.position.x < sp.endX) {
        sp.group.position.x = sp.startX;
      }
    });

    // Flickering billboards
    if (Math.random() > 0.98) {
      const idx = Math.floor(Math.random() * this.billboardMats.length);
      const bMat = this.billboardMats[idx];
      bMat.opacity = 0.4 + Math.random() * 0.6;
    }
  }
}
